import React, { useState } from 'react';
import { useBranches, useCreateBranch, useStaff, useUpdateBranch } from '@/hooks/use-api';
import type { Branch, User } from '@shared/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Pencil, Plus, Power } from 'lucide-react';
import { toast } from 'sonner';

export function BranchesTab() {
  const { data: branches } = useBranches(true);
  const { data: staffData, isLoading: staffLoading } = useStaff(true);
  const createBranch = useCreateBranch();
  const updateBranch = useUpdateBranch();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [newBranch, setNewBranch] = useState({ name: '', managerId: '', location: '' });
  const [editForm, setEditForm] = useState({ name: '', managerId: '', location: '' });

  const managers = staffData?.items.filter((s) => s.role === 'Manager') || [];

  const handleCreate = async () => {
    if (!newBranch.name || !newBranch.managerId) { toast.error('Name and Manager are required'); return; }
    try {
      await createBranch.mutateAsync(newBranch);
      toast.success('Branch established');
      setIsCreateOpen(false);
      setNewBranch({ name: '', managerId: '', location: '' });
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed to create branch'); }
  };

  const handleEdit = async () => {
    if (!editingBranch) return;
    try {
      await updateBranch.mutateAsync({ id: editingBranch.id, data: editForm });
      toast.success('Branch updated');
      setEditingBranch(null);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed to update branch'); }
  };

  const handleToggleActive = async (branch: Branch) => {
    try {
      await updateBranch.mutateAsync({ id: branch.id, data: { active: !branch.active } });
      toast.success(branch.active ? 'Branch suspended' : 'Branch reactivated');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed to update branch status'); }
  };

  const openEditor = (branch: Branch) => {
    setEditingBranch(branch);
    setEditForm({ name: branch.name, managerId: branch.managerId, location: branch.location || '' });
  };

  return (
    <>
      <Card className="overflow-hidden rounded-3xl border border-border bg-card shadow-glass">
        <CardHeader className="flex flex-col items-start justify-between gap-4 border-b bg-muted/30 p-8 sm:flex-row sm:items-center">
          <div>
            <CardTitle className="text-foreground font-black uppercase tracking-tight">Regional Branches</CardTitle>
            <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Full CRUD: create, edit name/manager/location, and suspend/reactivate.</CardDescription>
          </div>
          <Button onClick={() => setIsCreateOpen(true)} className="h-11 rounded-xl bg-primary px-6 font-black uppercase tracking-tight shadow-lg shadow-primary/20 hover:bg-primary/90">
            <Plus className="mr-2 h-4 w-4" />Establish Branch
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow className="border-border">
                  <TableHead className="py-5 pl-8 text-[10px] font-black uppercase tracking-widest text-muted-foreground">Branch Name</TableHead>
                  <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Location</TableHead>
                  <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Manager</TableHead>
                  <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Status</TableHead>
                  <TableHead className="pr-8 text-right text-[10px] font-black uppercase tracking-widest text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {branches?.map((branch) => (
                  <TableRow key={branch.id} className="border-border hover:bg-muted/20">
                    <TableCell className="pl-8 font-black tracking-tight text-foreground">{branch.name}</TableCell>
                    <TableCell className="text-xs font-bold uppercase tracking-tight text-muted-foreground">{branch.location || 'N/A'}</TableCell>
                    <TableCell className="font-bold text-muted-foreground">
                      {staffData?.items.find((s) => s.id === branch.managerId)?.name || branch.managerId}
                    </TableCell>
                    <TableCell>
                      <Badge className={branch.active ? 'border-none bg-emerald-500/10 text-emerald-600' : 'border-none bg-destructive/10 text-destructive'}>
                        {branch.active ? 'Operational' : 'Suspended'}
                      </Badge>
                    </TableCell>
                    <TableCell className="pr-8">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" className="rounded-xl font-black uppercase text-muted-foreground" onClick={() => openEditor(branch)}>
                          <Pencil className="mr-2 h-4 w-4" />Edit
                        </Button>
                        <Button variant="ghost" size="sm" className="rounded-xl font-black uppercase text-destructive" onClick={() => handleToggleActive(branch)}>
                          <Power className="mr-2 h-4 w-4" />{branch.active ? 'Suspend' : 'Activate'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Branch Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="rounded-[2rem] border-border p-8 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase tracking-tight text-foreground">Establish New Branch</DialogTitle>
            <DialogDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Define a new operational territory and assign a manager.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-6">
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Branch Name</Label>
              <Input placeholder="e.g. Northern Sector" value={newBranch.name} onChange={(e) => setNewBranch((c) => ({ ...c, name: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
            </div>
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Location</Label>
              <Input placeholder="City, District" value={newBranch.location} onChange={(e) => setNewBranch((c) => ({ ...c, location: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
            </div>
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Assign Manager</Label>
              <Select value={newBranch.managerId} onValueChange={(v) => setNewBranch((c) => ({ ...c, managerId: v }))}>
                <SelectTrigger className="h-12 rounded-xl border-border bg-muted/30"><SelectValue placeholder="Select Manager" /></SelectTrigger>
                <SelectContent className="rounded-xl">
                  {managers.length ? managers.map((m) => <SelectItem key={m.id} value={m.id} className="font-bold">{m.name}</SelectItem>) : <SelectItem value="empty" disabled>No managers available</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setIsCreateOpen(false)} className="h-12 rounded-xl px-6 text-xs font-black uppercase text-muted-foreground">Cancel</Button>
            <Button onClick={handleCreate} className="h-12 rounded-xl bg-primary px-8 font-black uppercase tracking-tight shadow-lg shadow-primary/20 hover:bg-primary/90" disabled={createBranch.isPending}>
              {createBranch.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Establish Branch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Branch Modal */}
      <Dialog open={!!editingBranch} onOpenChange={(open) => !open && setEditingBranch(null)}>
        <DialogContent className="rounded-[2rem] border-border p-8 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase tracking-tight text-foreground">Edit Branch</DialogTitle>
            <DialogDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Update branch details and manager assignment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-6">
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Branch Name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm((c) => ({ ...c, name: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
            </div>
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Location</Label>
              <Input value={editForm.location} onChange={(e) => setEditForm((c) => ({ ...c, location: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
            </div>
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Manager</Label>
              <Select value={editForm.managerId} onValueChange={(v) => setEditForm((c) => ({ ...c, managerId: v }))}>
                <SelectTrigger className="h-12 rounded-xl border-border bg-muted/30"><SelectValue placeholder="Select Manager" /></SelectTrigger>
                <SelectContent className="rounded-xl">
                  {managers.length ? managers.map((m) => <SelectItem key={m.id} value={m.id} className="font-bold">{m.name}</SelectItem>) : <SelectItem value="empty" disabled>No managers</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setEditingBranch(null)} className="h-12 rounded-xl px-6 text-xs font-black uppercase text-muted-foreground">Cancel</Button>
            <Button onClick={handleEdit} className="h-12 rounded-xl bg-primary px-8 font-black uppercase tracking-tight shadow-lg shadow-primary/20 hover:bg-primary/90" disabled={updateBranch.isPending}>
              {updateBranch.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
